'use client'

import { useEffect, useRef, useState } from 'react'
import { getLatestEvent } from 'src/utils/raceUtils'
import type { MarbleGame } from 'src/types/domain/Game'
import { useVisualCrashStatus } from './useVisualCrash'
import { CRASH_RATE, CRASH_REVEAL_DELAY_SECONDS } from './crashCurve'
import { crashEventClockOriginMs } from './crashHold'
import './crash-hud-overlay.scss'

type Props = Readonly<{
  game: MarbleGame
}>

type HudPhase = 'idle' | 'arming' | 'live' | 'crash'

// Crash-curve contract (CRASH_RATE, reveal delay) imported from the shared
// leaf ./crashCurve module so the HUD stays self-contained yet in lockstep with
// the engine / Go backend: the displayed multiplier is
// exp(CRASH_RATE · max(0, elapsed − reveal)).

// Tension vignette peaks at this alpha as the multiplier approaches ~10x. It's
// computed imperatively off a 4Hz interval (below) and quantised to coarse
// steps, then written to a CSS custom property on the overlay root — so the
// screen-sized red radial only re-composites a handful of times per round
// (never per frame, which is costly on slow Androids, and the eye can't read
// <0.05 alpha deltas on a vignette anyway).
const VIGNETTE_MAX_ALPHA = 0.22
const VIGNETTE_STEP = 0.05
const VIGNETTE_TOP_MULT = 10
const VIGNETTE_TICK_MS = 250

/**
 * LOCKDOWN edge-HUD chrome, layered over the CCTV video for crash games only.
 *
 * The WIDGET owns the multiplier and all status text now (see
 * `CrashMultiplierDisplay` — the video carries no baked game text). This
 * overlay owns ONLY the frame GEOMETRY + heat: four corner target brackets, a
 * scan sweep, and a tension vignette.
 *
 * Owner request: the mono telemetry stack (● REC + round id) and the ticking
 * mm:ss timer were removed — they read as fake CCTV chrome (the baked engine
 * footage carries none either; only the frame-time beacon lives bottom-left,
 * engine-side). The brackets + scan sweep stay as quiet ambient chrome.
 *
 * Read-only: it derives its phase purely from the round's race events + a couple
 * of coarse timers. It does NOT run a per-frame multiplier loop — the vignette
 * heat is computed imperatively and written to a CSS variable — so the HUD
 * subtree never re-renders at frame rate. It never submits or mutates bets, so
 * it's safe to mount alongside the betting panel.
 *
 * Phases:
 *   - idle    → betting / scheduled: overlay hidden.
 *   - arming  → race_started, reveal window (mult flat at 1.00x): the "power
 *               on" — brackets draw in, one scan sweep.
 *   - live    → past the reveal window (multiplier climbing): faint idle chrome,
 *               ambient slow sweep, vignette easing red.
 *   - crash   → round crashed: glitch + red bracket snap + vignette flash,
 *               held ~1.5s then faded (kept mounted until the next race_ready).
 */
export function CrashHudOverlay({ game }: Props) {
  const race = game.races[0]
  const raceEvents = race?.raceEvents ?? []

  // Only the FACT of the crash matters here (phase flip) — the crash value +
  // stamp are owned by CrashMultiplierDisplay.
  const crashedEvent = raceEvents.find(
    (e) => e.eventType === 'race_crashed' || e.eventType === 'race_ended'
  )

  // Phase is driven purely by race events + coarse timers — NOT by a per-frame
  // multiplier. `isClimbing` = the round is running; `pastReveal` = the flat
  // reveal window has elapsed (arming → live), a single one-shot transition.
  const isClimbing = getLatestEvent(raceEvents)?.eventType === 'race_started'
  // The event clock's origin: race_started's stamp, moved back by 3 − R on a
  // short-hold round (crashHold.ts), so the reveal below ends R after it.
  const startTs = crashEventClockOriginMs(raceEvents)

  const rootRef = useRef<HTMLDivElement>(null)

  // arming → live: flip once the reveal-delay window has elapsed. A one-shot
  // timeout (not a per-frame check) is enough — this transition happens exactly
  // once per round.
  const [pastReveal, setPastReveal] = useState(false)
  useEffect(() => {
    if (!isClimbing || startTs === undefined) {
      setPastReveal(false)
      return
    }
    const msUntilClimb = startTs + CRASH_REVEAL_DELAY_SECONDS * 1000 - Date.now()
    if (msUntilClimb <= 0) {
      setPastReveal(true)
      return
    }
    setPastReveal(false)
    const id = setTimeout(() => setPastReveal(true), msUntilClimb)
    return () => clearTimeout(id)
  }, [isClimbing, startTs])

  // VISUAL-CRASH GATE: while the store is 'armed' (the dock's useVisualCrash
  // driver is live and the crash frame hasn't reached this viewer), hold the
  // glitch/red-snap in the LIVE look — the whole crash payoff lands together
  // when the hit is actually on screen. 'undriven' (spectators) falls back to
  // event-driven, exactly the old behaviour.
  const visualCrashStatus = useVisualCrashStatus()
  const hudPhase: HudPhase = crashedEvent
    ? visualCrashStatus === 'armed'
      ? 'live'
      : 'crash'
    : isClimbing
      ? pastReveal
        ? 'live'
        : 'arming'
      : 'idle'

  // Tension vignette heat — computed imperatively off a 4Hz interval and written
  // to a CSS custom property on the root ONLY when the quantised alpha changes.
  // No per-tick setState, so the screen-sized radial re-composites a few times a
  // round instead of 60×/s.
  useEffect(() => {
    if (hudPhase !== 'live' || startTs === undefined) return
    const el = rootRef.current
    if (!el) return
    let lastAlpha = -1
    const writeHeat = () => {
      const elapsed = (Date.now() - startTs) / 1000
      const mult = Math.exp(CRASH_RATE * Math.max(0, elapsed - CRASH_REVEAL_DELAY_SECONDS))
      const raw = Math.min(
        VIGNETTE_MAX_ALPHA,
        Math.max(0, ((mult - 1) / (VIGNETTE_TOP_MULT - 1)) * VIGNETTE_MAX_ALPHA)
      )
      const quantised = Math.round(raw / VIGNETTE_STEP) * VIGNETTE_STEP
      if (quantised !== lastAlpha) {
        lastAlpha = quantised
        el.style.setProperty('--crash-vignette-alpha', String(quantised))
      }
    }
    writeHeat()
    const id = setInterval(writeHeat, VIGNETTE_TICK_MS)
    return () => clearInterval(id)
  }, [hudPhase, startTs])

  // Hold the crash stamp, then fade the whole overlay (stays mounted until the
  // next round's race_ready flips us back to idle).
  const [crashFading, setCrashFading] = useState(false)
  useEffect(() => {
    if (hudPhase !== 'crash') {
      setCrashFading(false)
      return
    }
    const id = setTimeout(() => setCrashFading(true), 1500)
    return () => clearTimeout(id)
  }, [hudPhase])

  if (hudPhase === 'idle') return null

  const rootClass = [
    'crash-hud',
    `crash-hud--${hudPhase}`,
    crashFading ? 'crash-hud--fading' : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={rootRef} className={rootClass} aria-hidden>
      {/* Tension vignette — kept outside the glitching chrome so the flicker
          never jerks the whole tint. Red at the edges; alpha is written to
          `--crash-vignette-alpha` on the root imperatively (see the 4Hz effect
          above); snaps hot on crash via CSS. */}
      <div className="crash-hud__vignette" />

      {/* Chrome (brackets / scan) — this is what glitches on crash, so the
          vignette above stays steady. The ● REC telemetry, round id and mm:ss
          timer were removed on owner request (fake-CCTV chrome). */}
      <div className="crash-hud__chrome">
        <span className="crash-hud__bracket crash-hud__bracket--tl" />
        <span className="crash-hud__bracket crash-hud__bracket--tr" />
        <span className="crash-hud__bracket crash-hud__bracket--bl" />
        <span className="crash-hud__bracket crash-hud__bracket--br" />

        <span className="crash-hud__scan" />
      </div>
    </div>
  )
}
